import { useState, useCallback, useEffect } from "react";
import { useRouter } from "next/router";

import clsx from "clsx";

import ApiError from "api/Error";

import Tag from "ui-components/Tag";
import Modal from "ui-components/Modal";
import Button from "ui-components/Button";
import ButtonSwitch from "ui-components/Button-Switch";

import {
  SingleColumnSelect,
  MultiColumnSelect,
} from "modules/data-files/elements/Column-Select";

import * as flowConfig from "flows/market-mix-modelling/config";

import { useGetCreate } from "./api";
import {
  useInputFile,
  useTimeColumn,
  useTargetColumn,
  // useGroupingColumns,
  useSpendColumns,
  useActivityColumns,
} from "./store";

import SelectData from "./Select-Data";
import Divider from "ui-components/Divider";
import {
  useFetchDataFile,
  useFetchDeducedColumnTypes,
} from "modules/data-files/api";

const CreateProject = () => {
  const router = useRouter();
  const data = router.query.data as string;
  const { push } = router;

  const [currentFile, setCurrentFile] = useInputFile();
  const [timestampColumn, setTimestampColumn] = useTimeColumn();
  const [targetColumn, setTargetColumn] = useTargetColumn();
  // const [groupingColumns, setGroupingColumns] = useGroupingColumns();
  const [spendColumns, setSpendColumns] = useSpendColumns();
  const [activityColumns, setActivityColumns] = useActivityColumns();

  const [createProject, submitStatus, error] = useGetCreate();

  useEffect(() => {
    if (data && data !== currentFile) {
      setCurrentFile(data);
    }
  }, [data, currentFile]);

  const { data: fileDetails } = useFetchDataFile(currentFile);
  const types = useFetchDeducedColumnTypes(currentFile);

  const candidateColumns = (fileDetails?.columns || [])
    .filter((c) => c !== timestampColumn)
    .filter((c) => c !== targetColumn)
    .filter((c) => types[c] === "int" || types[c] === "float");

  const nonSpendNonActivity = candidateColumns
    .filter((c) => !spendColumns.includes(c))
    .filter((c) => !activityColumns.includes(c));

  const create = useCallback(async () => {
    const resp = await createProject();
    if (resp?.success) {
      push(`/${flowConfig.FLOW_NAME}/${resp.project.id}`);
    }
  }, [createProject, push]);

  const [showColumnSettingsModal, setColumnSettingsModal] = useState(false);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <SelectData />
      {currentFile && (
        <>
          <div className="flex mt-2 pt-2">
            <div className="w-1/4">
              <label htmlFor={"target-column"} className="font-bold">
                2. Select Sales Column:
              </label>
            </div>
            <div className="w-full">
              <SingleColumnSelect
                name="sales-column"
                fileId={currentFile}
                currentSelection={targetColumn}
                onChange={(t) => {
                  setTargetColumn(t);
                  if (spendColumns.includes(t)) {
                    setSpendColumns(spendColumns.filter((c) => c !== t));
                  }
                  if (activityColumns.includes(t)) {
                    setActivityColumns(activityColumns.filter((c) => c !== t));
                  }
                }}
                allowedTypes={["numeric"]}
                placeholder="Select Sales Column"
              />
            </div>
          </div>
          <div className="flex mt-2 pt-2">
            <div className="w-1/4">
              <label htmlFor={"time-column"} className="font-bold">
                3. Select Time-Stamp Column:
              </label>
            </div>
            <div className="w-full">
              <SingleColumnSelect
                name="time-column"
                fileId={currentFile}
                currentSelection={timestampColumn}
                onChange={setTimestampColumn}
                allowedTypes={["numeric", "date"]}
                placeholder="Select Time Column"
              />
            </div>
          </div>

          {timestampColumn && targetColumn && (
            <div>
              <Divider />
              <p>
                <span className="font-bold">4. Column Settings:</span>{" "}
                <span
                  className="text-primary-main cursor-pointer font-semibold uppercase"
                  onClick={() => setColumnSettingsModal(true)}
                >
                  CHANGE COLUMN SETTINGS
                </span>
              </p>
              <Modal
                show={showColumnSettingsModal}
                id="column-settings"
                title="Set Column Settings"
                handleClose={() => setColumnSettingsModal(false)}
              >
                <div>
                  {candidateColumns.map((c) => (
                    <div
                      key={c}
                      className="grid grid-cols-3 pt-2 mt-2 border-t first:border-t-0"
                    >
                      <div className="col-span-2">
                        <p className="font-bold">{c}</p>
                      </div>
                      <ButtonSwitch<"spend" | "activity" | "none">
                        options={[
                          { key: "spend", label: "SPEND" },
                          { key: "activity", label: "ACTIVITY" },
                          { key: "none", label: "NEITHER" },
                        ]}
                        current={
                          spendColumns.includes(c)
                            ? "spend"
                            : activityColumns.includes(c)
                            ? "activity"
                            : "none"
                        }
                        onClick={(k) => {
                          switch (k) {
                            case "none":
                              if (spendColumns.includes(c)) {
                                setSpendColumns((spend) =>
                                  spend.filter((s) => s !== c)
                                );
                              }
                              if (activityColumns.includes(c)) {
                                setActivityColumns((activity) =>
                                  activity.filter((a) => a !== c)
                                );
                              }
                              return;
                            case "spend":
                              if (!spendColumns.includes(c)) {
                                setSpendColumns([...spendColumns, c]);
                              }
                              if (activityColumns.includes(c)) {
                                setActivityColumns((activity) =>
                                  activity.filter((a) => a !== c)
                                );
                              }
                              return;
                            case "activity":
                              if (spendColumns.includes(c)) {
                                setSpendColumns((spend) =>
                                  spend.filter((s) => s !== c)
                                );
                              }
                              if (!activityColumns.includes(c)) {
                                setActivityColumns([...activityColumns, c]);
                              }
                              return;
                          }
                        }}
                        className="text-right"
                      />
                    </div>
                  ))}
                </div>
                <Divider />
                <p className="text-right">
                  <Button onClick={() => setColumnSettingsModal(false)}>
                    Close
                  </Button>
                </p>
              </Modal>
              <div className="grid grid-cols-3 pt-2 gap-6">
                <div className="py-1 px-2">
                  <div className="flex flex-wrap">
                    <span className="font-semibold pr-2">Spend Columns:</span>
                    {spendColumns.length === 0
                      ? "NONE"
                      : spendColumns.map((c) => <Tag key={c}>{c}</Tag>)}
                  </div>
                </div>
                <div className="py-1 px-2">
                  <div className="flex flex-wrap">
                    <span className="font-semibold pr-2">
                      Activity Columns:
                    </span>
                    {activityColumns.length === 0
                      ? "NONE"
                      : activityColumns.map((c) => <Tag key={c}>{c}</Tag>)}
                  </div>
                </div>
                <div className="py-1 px-2">
                  <div className="flex flex-wrap">
                    <span className="font-semibold pr-2">
                      Non-Spend and Non-Activity Columns:
                    </span>
                    {nonSpendNonActivity.length === 0
                      ? "NONE"
                      : nonSpendNonActivity.map((c) => <Tag key={c}>{c}</Tag>)}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* <div className="flex mt-2 pt-2">
            <div className="w-1/4">
              <label htmlFor={"spend-columns"} className="font-bold">
                4. Select Spend Columns:
              </label>
              <p className="help text-gray-700 text-sm">
                Optional. You can change this later as well.
              </p>
            </div>
            <div className="w-full">
              <MultiColumnSelect
                name="spend-columns"
                fileId={currentFile}
                currentSelection={spendColumns}
                onChange={setSpendColumns}
                allowedTypes={["numeric"]}
                placeholder="Select Spend Columns"
                help="Spend Columns, e.g., TV, Internet, etc. Select anything which has converted to currency. S-Curves are automatically computed for these params."
              />
            </div>
          </div>
          <div className="flex mt-2 pt-2">
            <div className="w-1/4">
              <label htmlFor={"activity-columns"} className="font-bold">
                5. Select Activity Columns:
              </label>
              <p className="help text-gray-700 text-sm">
                Optional. You can change this later as well.
              </p>
            </div>
            <div className="w-full">
              <MultiColumnSelect
                name="activity-columns"
                fileId={currentFile}
                currentSelection={activityColumns}
                onChange={setActivityColumns}
                allowedTypes={["numeric"]}
                placeholder="Select Activity Columns"
                help="Activity Columns, e.g., # of calls, # of stores, promotions run, etc. Select salesforce related columns which ARENT money. S-Curves are automatically computed for these params."
              />
            </div>
          </div> */}
          {/* <div className="flex mt-2 pt-2">
            <div className="w-1/4">
              <label htmlFor={"grouping-columns"} className="font-bold">
                4. Select Grouping Columns:
              </label>
            </div>
            <div className="w-full">
              <MultiColumnSelect
                name="grouping-columns"
                fileId={currentFile}
                currentSelection={groupingColumns}
                onChange={setGroupingColumns}
                allowedTypes={["numeric", "char"]}
                placeholder="Select Grouping Columns"
                help="Optional"
              />
            </div>
          </div> */}
        </>
      )}
      {submitStatus === "completed" && error && <ApiError error={error} />}
      {currentFile && targetColumn && timestampColumn && (
        <div className="text-center my-4 py-4">
          <div className="flex flex-wrap -mx-3">
            <div className="w-full px-3">
              <button
                className={clsx(
                  "btn px-0",
                  "text-white",
                  "bg-indigo-600 hover:bg-indigo-700",
                  "w-full",
                  "relative",
                  "flex items-center"
                  // submitStatus === "in-progress" && "cursor-not-allowed"
                )}
                disabled={submitStatus === "in-progress"}
                onClick={create}
              >
                {submitStatus === "in-progress" && (
                  <svg
                    className="animate-spin h-5 w-5 mr-3  text-white"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                {submitStatus === "in-progress"
                  ? "Creating..."
                  : "Create New Project"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateProject;
